import { BannerType, Languages } from '@/constants'
import { useArea, useLocalStorage } from '@/hooks'
import { useCookie } from '@/hooks/useCookie'
import { useCookie1Y } from '@/hooks/useCookie1Y'
import type {
  BannerList,
  Keywords,
  Menu,
  ProductCompare,
  ProductViewed,
  Province,
  Region,
  SystemConfig,
  Wallet,
} from '@/types'
import { api } from '@/utils'
import constate from 'constate'
import { getCookie } from 'cookies-next'
import { useEffect, useMemo, useState } from 'react'
import useLocalStorageState from 'use-local-storage-state'

type Props = {
  stores: Province[] | null
  regions: Region[]
  area: string | null
  language: Languages
  systemConfig: SystemConfig
  menus: Menu[] | null
  globalHeaderMenu: Menu[] | null
  bottomMenu: Menu[] | null
  banner: BannerList[] | null
  keywords: Keywords[] | null
  wallet?: Wallet
}

const StoreContainer = (props: Props) => {
  const [isOpenPopupLocation, setIsOpenPopupLocation] = useState(false)
  const [isOpenShare, setIsOpenShare] = useState(false)
  const [isDisplayAreaPopup, setIsDisplayAreaPopup] = useLocalStorage<boolean>(
    'areaPopup',
    true
  )
  const currentRegion = getCookie('sale-region')
  const defaultSaleRegion = process.env.NEXT_PUBLIC_SALE_REGION_DEFAULT
  const [productCompares, setProductCompares] = useLocalStorageState<
    ProductCompare[]
  >('productCompare', { defaultValue: [{}, {}, {}] })

  const [productViewed, setProductViewed] = useLocalStorage<ProductViewed[]>(
    'productViewed',
    []
  )
  const [isOpenComparePopup, setIsOpenComparePopup] = useState(false)
  const [isOpenAddComparePopup, setIsOpenAddComparePopup] = useState(false)
  const [isOpenComingSoon, setIsOpenComingSoon] = useState(false)
  const [catViewId, setCatViewId] = useState<string | null>(null)
  const [stores, setStores] = useState<Province[]>([])
  const [regions, setRegions] = useState<Region[]>([])
  const [megaMenu, setMegaMenu] = useState<Menu[]>(props.menus || [])
  const [globalHeaderMenu, setGlobalHeaderMenu] = useState<Menu[]>(
    props.globalHeaderMenu || []
  )
  const [bottomMenu] = useState<Menu[]>(props.bottomMenu || [])
  const [banner, setBanner] = useState<BannerList[]>(props.banner || [])
  const [province, setProvince] = useCookie<string | null>('province', null)
  const [destinationCode, setDestinationCode] = useCookie<string | null>(
    'destinationCode',
    null
  )
  const [isPreOrder, setIsPreOrder] = useCookie<string | null>(
    'isPreOrder',
    null
  )
  const [keywords, setKeywords] = useState<Keywords[]>(props.keywords || [])
  const [wallet, setWallet] = useState<Wallet>()
  const [regionChange, setRegionChange] = useState<string>('')
  const [selectedLang, setSelectedLang] = useCookie<
    Languages | undefined | null
  >('language', props.language || Languages.VN)

  const {
    addressVersion,
    setAddressVersion,
    provinces,
    provinceAvailable,
    setProvinceAvailable,
    districts,
    wards,
    setWards,
    provinceCode,
    setProvinceCode,
    districtCode,
    setDistrictCode,
    wardCode,
    setWardCode,
    provinceOfRegion,
    setProvinceOfRegion,
    areasLoading,
    areasError,
    getProvinces,
    getDistricts,
    getWards,
  } = useArea({ currentRegion: currentRegion as string | null })

  // Keep for compatibility
  const [wardDistricts, setWardDistricts] = useState<Province[]>([])

  const [area, setArea] = useCookie1Y<string | null>('area', null)
  const [areaId, setAreaId] = useCookie1Y<string | null>('area-id', null)
  const [saleRegion, setSaleRegion] = useCookie1Y<string | null>(
    'sale-region',
    null
  )

  const [isChangeRegionModal, setIsChangeRegionModal] = useState(false)

  useEffect(() => {
    if (props.wallet) {
      setWallet(props.wallet)
    }
  }, [props.wallet])

  useEffect(() => {
    if (regions) {
      const defaultStore = regions.find(e => e?.code === defaultSaleRegion)
      if (defaultStore && defaultSaleRegion && !saleRegion) {
        setSaleRegion(defaultStore?.code)
      }
    }
  }, [regions])

  useEffect(() => {
    if (props.menus) {
      setMegaMenu(props.menus)
    }
  }, [props.menus])
  useEffect(() => {
    if (props.globalHeaderMenu && !globalHeaderMenu) {
      setGlobalHeaderMenu(props.globalHeaderMenu)
    }
  }, [props.globalHeaderMenu])

  useEffect(() => {
    if (props.keywords && keywords.length == 0) {
      setKeywords(props.keywords)
    }
  }, [props.keywords])

  useEffect(() => {
    if (props.banner) {
      setBanner(props.banner)
    }
  }, [props.banner])

  useEffect(() => {
    if (props.regions) {
      setRegions(props.regions)
    }
  }, [props.regions])

  useEffect(() => {
    if (props.language && !selectedLang) {
      setSelectedLang(props.language)
    }
  }, [props.language])

  const topBanner = useMemo(() => {
    const topBanner = banner.find(e => e.type === BannerType.TOP_BAR)
    return topBanner
  }, [banner])

  const sideBarDataBanner = useMemo(() => {
    const sideBarDataBanner = banner.find(e => e.type === BannerType.SIDEBAR)
    return sideBarDataBanner ? sideBarDataBanner.items : []
  }, [banner])

  useEffect(() => {
    if (provinceCode && provinceCode !== '') {
      api(`api/area/${provinceCode}/ward?`, {
        method: 'get',
        baseURL: process.env.NEXT_PUBLIC_IAM_API_URL,
      }).then(async result => {
        if (result instanceof Error) {
          return
        }
        setWardDistricts(result?.data)
      })
    }
  }, [provinceCode])
  return {
    province,
    setProvince,
    area,
    setArea,
    areaId,
    setAreaId,
    setSelectedLang,
    setDestinationCode,
    destinationCode,
    selectedLang,
    stores,
    banner,
    megaMenu,
    topBanner,
    sideBarDataBanner,
    keywords,
    setStores,
    isOpenPopupLocation,
    setIsOpenPopupLocation,
    isOpenShare,
    setIsOpenShare,
    isDisplayAreaPopup,
    bottomMenu,
    setIsDisplayAreaPopup,
    isOpenComingSoon,
    setIsOpenComingSoon,
    productCompares,
    setProductCompares,
    isOpenComparePopup,
    setIsOpenComparePopup,
    isOpenAddComparePopup,
    setIsOpenAddComparePopup,
    productViewed,
    setProductViewed,
    catViewId,
    setCatViewId,
    isPreOrder,
    setIsPreOrder,
    saleRegion,
    setSaleRegion,
    regions,
    setRegions,
    currentRegion,
    provinces,
    districts,
    wards,
    getDistricts,
    addressVersion,
    setAddressVersion,
    getProvinces,
    provinceCode,
    setWardDistricts,
    wardDistricts,
    getWards,
    setWards,
    setProvinceCode,
    areasError,
    areasLoading,
    districtCode,
    setWardCode,
    wardCode,
    setDistrictCode,
    provinceOfRegion,
    setProvinceOfRegion,
    provinceAvailable,
    setProvinceAvailable,
    isChangeRegionModal,
    setIsChangeRegionModal,
    regionChange,
    setRegionChange,
    globalHeaderMenu,
    wallet,
    setWallet,
  }
}

export const [StoreProvider, useStore] = constate(StoreContainer)
